declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary'];

    white: string;
    border: string;

    green: ColorOptions;
    red: ColorOptions;
    amber: ColorOptions;
    blue: ColorOptions;

    orange: ColorOptions;
    brown: ColorOptions;
    teal: ColorOptions;
    indigo: ColorOptions;
    deepPurple: ColorOptions;
    yellow: ColorOptions;
    lightGreen: ColorOptions;
    blueGrey: ColorOptions;
    purple: ColorOptions;
    pink: ColorOptions;

    text: TypeText;
    DataGrid: { bg: string; headerBg: string; pinnedBg: string };
  }

  interface PaletteOptions {
    tertiary: PaletteOptions['primary'];

    white: string;
    border: string;

    green: ColorOptions;
    red: ColorOptions;
    amber: ColorOptions;
    blue: ColorOptions;

    orange: ColorOptions;
    brown: ColorOptions;
    teal: ColorOptions;
    indigo: ColorOptions;
    deepPurple: ColorOptions;
    yellow: ColorOptions;
    lightGreen: ColorOptions;
    blueGrey: ColorOptions;
    purple: ColorOptions;
    pink: ColorOptions;

    DataGrid: { bg: string; headerBg: string; pinnedBg: string };
  }

  interface TypeText {
    primary: string;
    secondary: string;
    tertiary: string;
    white: string;
  }

  interface ColorOptions {
    '100': string;
    '200': string;
    '300': string;
    '400': string;
    '500': string;
    '600': string;
  }
}

const greyColor = {
  '100': '#F6FAFC',
  '200': '#EFF4F5',
  '300': '#DCE4E5',
  '400': '#C0CACC',
  '500': '#8D9B9E',
  '600': '#667C81',
  '700': '#597B82',
};

const typoColor = {
  primary: '#2F3232',
  secondary: '#707676',
  tertiary: '#AAADAD',
  white: '#FFFFFF',
};

const elementsColor = {
  border: '#E5E5E5',
  background: '#F9F9F9',
  white: '#FFFFFF',
};

const greenColor = {
  '100': '#E6F7E9',
  '200': '#96DB9D',
  '300': '#6DC976',
  '400': '#0AA849',
  '500': '#4B9256',
  '600': '#3E7747',
};

const redColor = {
  '100': '#FBE7E8',
  '200': '#F67D85',
  '300': '#EB5761',
  '400': '#DC0D1A',
  '500': '#D2535C',
  '600': '#B3261E',
};

const amberColor = {
  '100': '#FCF5E6',
  '200': '#F6C86D',
  '300': '#F2BC50',
  '400': '#F9B122',
  '500': '#ECAD33',
  '600': '#E9A625',
};

const blueColor = {
  '100': '#E6F1F7',
  '200': '#6FB7E1',
  '300': '#55BCF0',
  '400': '#0D90DC',
  '500': '#3791C5',
  '600': '#407695',
};

const orange = {
  '100': '#FCEDE6',
  '200': '#F29E74',
  '300': '#EB8552',
  '400': '#D95A1A',
  '500': '#B25B36',
  '600': '#8C4323',
};

const brown = {
  '100': '#F7EADF',
  '200': '#D1A47D',
  '300': '#B8895F',
  '400': '#8C633F',
  '500': '#6B503B',
  '600': '#523F31',
};

const teal = {
  '100': '#E1F7F5',
  '200': '#61D9C8',
  '300': '#58D1C5',
  '400': '#28C7B7',
  '500': '#41928E',
  '600': '#337A71',
};

const indigo = {
  '100': '#E6E6FA',
  '200': '#7E7EE6',
  '300': '#5E5EE0',
  '400': '#1515D1',
  '500': '#4246A6',
  '600': '#2E3185',
};

const deepPurple = {
  '100': '#F0E6FA',
  '200': '#B27EE6',
  '300': '#9F5EE0',
  '400': '#7315D1',
  '500': '#7142A6',
  '600': '#572E85',
};

const yellow = {
  '100': '#FCFAE8',
  '200': '#F7E98F',
  '300': '#F2E06D',
  '400': '#F0D52B',
  '500': '#C7B03C',
  '600': '#9E8B2C',
};

const lightGreen = {
  '100': '#F1FAE3',
  '200': '#BCEB7A',
  '300': '#A8E05A',
  '400': '#80C71E',
  '500': '#779940',
  '600': '#617A37',
};

const blueGrey = {
  '100': '#E4ECF7',
  '200': '#97AFD1',
  '300': '#7792B8',
  '400': '#567094',
  '500': '#4B5D73',
  '600': '#3D4A59',
};

const purple = {
  '100': '#F8E6FA',
  '200': '#DD7EE6',
  '300': '#D65EE0',
  '400': '#C115D1',
  '500': '#9A42A6',
  '600': '#7B2E85',
};

const pink = {
  '100': '#FAE6ED',
  '200': '#E67EA1',
  '300': '#E05E8A',
  '400': '#D11554',
  '500': '#A64267',
  '600': '#852E4E',
};

export const palette = {
  text: typoColor,

  grey: greyColor,
  green: greenColor,
  red: redColor,
  amber: amberColor,
  blue: blueColor,
  white: elementsColor.white,

  orange,
  brown,
  teal,
  indigo,
  deepPurple,
  yellow,
  lightGreen,
  blueGrey,
  purple,
  pink,

  background: {
    paper: elementsColor.white,
    default: elementsColor.background,
  },
  border: elementsColor.border,

  tertiary: {
    main: typoColor.white,
    dark: typoColor.tertiary,
    contrastText: typoColor.primary,
  },
  success: {
    main: greenColor['100'],
    contrastText: greenColor['600'],
    dark: greenColor['500'],
  },
  error: {
    main: redColor['100'],
    contrastText: redColor['600'],
    dark: redColor['500'],
  },
  warning: {
    main: amberColor['100'],
    contrastText: amberColor['600'],
    dark: amberColor['500'],
  },
  info: {
    main: blueColor['100'],
    contrastText: blueColor['600'],
    dark: blueColor['500'],
  },
  DataGrid: {
    bg: elementsColor.background,
    headerBg: greyColor[100],
    pinnedBg: 'transparent',
  },
};
